import React, { useState, useEffect, useCallback } from "react";
import { api, useBusiness } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const ACCOUNT_TYPES = [
  { value: "income", label: "Income Account", color: "bg-blue-500", icon: "DollarSign" },
  { value: "profit", label: "Profit Account", color: "bg-[#1e3a5f]", icon: "TrendingUp" },
  { value: "owner_pay", label: "Owner's Pay", color: "bg-violet-500", icon: "User" },
  { value: "tax", label: "Tax Account", color: "bg-amber-500", icon: "FileText" },
  { value: "opex", label: "Operating Expenses", color: "bg-red-500", icon: "Building" }
];

const AccountIcon = ({ name, className = "w-5 h-5" }) => {
  const icons = {
    DollarSign: <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
    TrendingUp: <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
    User: <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    FileText: <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    Building: <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01M12 14h.01"/></svg>,
    ArrowRight: <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
    Repeat: <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>,
    Calendar: <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    Zap: <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  };
  return icons[name] || null;
};

const ProfitFirstAccounts = () => {
  const { selectedBusiness } = useBusiness();
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState({});
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [manualBankAccounts, setManualBankAccounts] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [quickTransfers, setQuickTransfers] = useState([]);
  const [transferSummary, setTransferSummary] = useState(null);
  const [autoSettings, setAutoSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAllocationDialog, setShowAllocationDialog] = useState(false);
  const [showQuickTransferDialog, setShowQuickTransferDialog] = useState(false);
  const [showAutoSettingsDialog, setShowAutoSettingsDialog] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const [formData, setFormData] = useState({
    account_source: "manual_bank",
    plaid_account_id: "",
    manual_bank_account_id: "",
    manual_account_name: "",
    manual_account_mask: "",
    manual_institution: "",
    allocation_percent: ""
  });

  const [allocationAmount, setAllocationAmount] = useState("");
  const [quickTransferForm, setQuickTransferForm] = useState({
    from_account_type: "",
    to_account_type: "",
    amount: "",
    notes: ""
  });
  const [autoSettingsForm, setAutoSettingsForm] = useState({
    enabled: false,
    revenue_source: "manual",
    default_revenue_amount: ""
  });
  const [savingAutoSettings, setSavingAutoSettings] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const [acctTypesRes, acctRes, linkedRes, manualRes, allocRes, qTransRes, summaryRes, settingsRes] = await Promise.all([
        api.get("/opturna/account-types"),
        api.get(`/opturna/accounts/${selectedBusiness.id}`),
        api.get("/plaid/accounts").catch(() => ({ data: [] })),
        api.get(`/manual-bank-accounts/${selectedBusiness.id}`),
        api.get(`/opturna/allocations/${selectedBusiness.id}`),
        api.get(`/opturna/quick-transfers/${selectedBusiness.id}`),
        api.get(`/opturna/transfer-summary/${selectedBusiness.id}`),
        api.get(`/opturna/auto-transfer/settings/${selectedBusiness.id}`)
      ]);
      setAccountTypes(acctTypesRes.data);
      setAccounts(acctRes.data);
      setLinkedAccounts(linkedRes.data);
      setManualBankAccounts(manualRes.data);
      setAllocations(allocRes.data);
      setQuickTransfers(qTransRes.data);
      setTransferSummary(summaryRes.data);
      setAutoSettings(settingsRes.data);
      setAutoSettingsForm({
        enabled: settingsRes.data.enabled || false,
        revenue_source: settingsRes.data.revenue_source || "manual",
        default_revenue_amount: settingsRes.data.default_revenue_amount?.toString() || ""
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!selectedType) return;
    try {
      let payload = {
        business_id: selectedBusiness.id,
        account_type: selectedType,
        account_source: formData.account_source,
        allocation_percent: parseFloat(formData.allocation_percent) || null
      };
      if (formData.account_source === "plaid") {
        payload.plaid_account_id = formData.plaid_account_id;
      } else if (formData.account_source === "manual_bank") {
        const selectedManualAccount = manualBankAccounts.find(a => a.id === formData.manual_bank_account_id);
        if (selectedManualAccount) {
          payload.manual_bank_account_id = formData.manual_bank_account_id;
          payload.manual_account_name = selectedManualAccount.account_name;
          payload.manual_account_mask = selectedManualAccount.account_number_last4;
          payload.manual_institution = selectedManualAccount.bank_name;
        }
      } else {
        payload.manual_account_name = formData.manual_account_name;
        payload.manual_account_mask = formData.manual_account_mask;
        payload.manual_institution = formData.manual_institution;
      }
      await api.post("/opturna/accounts", payload);
      toast.success(`${accountTypes[selectedType]?.name || "Account"} assigned successfully!`);
      setShowAddDialog(false);
      setFormData({ account_source: "manual_bank", plaid_account_id: "", manual_bank_account_id: "", manual_account_name: "", manual_account_mask: "", manual_institution: "", allocation_percent: "" });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add account");
    }
  };

  const handleCreateAllocation = async () => {
    if (!allocationAmount || parseFloat(allocationAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    try {
      await api.post("/opturna/allocations", {
        business_id: selectedBusiness.id,
        total_revenue: parseFloat(allocationAmount)
      });
      toast.success("Allocation created! Check your transfer checklist.");
      setShowAllocationDialog(false);
      setAllocationAmount("");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create allocation");
    }
  };

  const handleTransferComplete = async (transferId, completed) => {
    try {
      await api.put(`/opturna/transfers/${transferId}/complete?completed=${completed}`);
      fetchAll();
      toast.success(completed ? "Transfer marked as complete!" : "Transfer marked as pending");
    } catch (error) {
      toast.error("Failed to update transfer status");
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      await api.delete(`/opturna/accounts/${accountId}`);
      toast.success("Account removed");
      fetchAll();
    } catch (error) {
      toast.error("Failed to remove account");
    }
  };

  const handleQuickTransfer = async () => {
    const amount = parseFloat(quickTransferForm.amount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!quickTransferForm.from_account_type || !quickTransferForm.to_account_type) {
      toast.error("Select both accounts");
      return;
    }
    try {
      await api.post("/opturna/quick-transfer", {
        business_id: selectedBusiness.id,
        from_account_type: quickTransferForm.from_account_type,
        to_account_type: quickTransferForm.to_account_type,
        amount: amount,
        notes: quickTransferForm.notes || null
      });
      toast.success(`Transferred $${amount.toLocaleString()} successfully!`);
      setShowQuickTransferDialog(false);
      setQuickTransferForm({ from_account_type: "", to_account_type: "", amount: "", notes: "" });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transfer failed");
    }
  };

  const handleSaveAutoSettings = async () => {
    setSavingAutoSettings(true);
    try {
      await api.post("/opturna/auto-transfer/settings", {
        business_id: selectedBusiness.id,
        enabled: autoSettingsForm.enabled,
        revenue_source: autoSettingsForm.revenue_source,
        default_revenue_amount: parseFloat(autoSettingsForm.default_revenue_amount) || null
      });
      toast.success(autoSettingsForm.enabled ? "Auto-allocations enabled!" : "Auto-allocations disabled");
      setShowAutoSettingsDialog(false);
      fetchAll();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingAutoSettings(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(amount || 0);

  const getAccountTypeInfo = (type) => ACCOUNT_TYPES.find(t => t.value === type);
  const getAssignedAccount = (type) => accounts.find(a => a.account_type === type);
  const totalPercent = accounts.filter(a => a.account_type !== "income").reduce((sum, a) => sum + (a.allocation_percent || 0), 0);
  const configuredNonIncome = accounts.filter(a => a.account_type !== "income");

  if (!selectedBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-4">
        <h3 className="text-lg font-semibold text-stone-900 mb-2">No Business Selected</h3>
        <p className="text-stone-600">Please select or create a business first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20" data-testid="smart-accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">Smart Accounts</h1>
          <p className="text-stone-600 text-sm sm:text-base">Assign accounts & automate your Opturna allocations</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => setShowQuickTransferDialog(true)}
            disabled={configuredNonIncome.length < 2}
            className="flex-1 sm:flex-auto"
            data-testid="quick-transfer-btn"
          >
            <AccountIcon name="ArrowRight" className="w-4 h-4 mr-1.5" />
            Quick Transfer
          </Button>
          <Button
            onClick={() => setShowAllocationDialog(true)}
            className="bg-[#1e3a5f] hover:bg-[#2d5a8e] flex-1 sm:flex-auto"
            disabled={accounts.length < 2}
            data-testid="run-allocation-btn"
          >
            Run Allocation
          </Button>
        </div>
      </div>

      {/* Transfer Summary Banner */}
      {transferSummary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="transfer-summary">
          <Card className="border-l-4 border-l-[#1e3a5f]">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-stone-500 mb-1">Total Allocated</p>
              <p className="text-lg font-bold text-[#1e3a5f]">{formatCurrency(transferSummary.total_allocated)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-stone-500 mb-1">Pending</p>
              <p className="text-lg font-bold text-amber-600">{transferSummary.pending_allocations} allocation{transferSummary.pending_allocations !== 1 ? 's' : ''}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-violet-500">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-stone-500 mb-1">Quick Transfers</p>
              <p className="text-lg font-bold text-violet-600">{formatCurrency(transferSummary.total_quick_transferred)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-stone-500 mb-1">Next Allocation</p>
              <p className="text-lg font-bold text-emerald-600">{transferSummary.next_allocation_date ? new Date(transferSummary.next_allocation_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Auto-Transfer Toggle Card */}
      <Card className={`transition-all ${autoSettings?.enabled ? 'bg-emerald-50/50 border-emerald-200' : 'bg-stone-50/50'}`} data-testid="auto-transfer-card">
        <CardContent className="py-4 px-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${autoSettings?.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500'}`}>
                <AccountIcon name="Repeat" className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-sm">Automatic Allocations</h3>
                <p className="text-xs text-stone-500">
                  {autoSettings?.enabled
                    ? `Runs on 10th & 25th — ${formatCurrency(autoSettings?.default_revenue_amount)} per cycle`
                    : 'Automatically create allocation checklists on the 10th & 25th'}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowAutoSettingsDialog(true)} data-testid="configure-auto-transfer-btn">
              Configure
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Setup Status */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Account Setup</CardTitle>
            <Badge variant={accounts.length === 5 ? "default" : "secondary"} className={accounts.length === 5 ? "bg-[#1e3a5f]" : ""}>
              {accounts.length}/5 Configured
            </Badge>
          </div>
          <Progress value={(accounts.length / 5) * 100} className="h-2 mt-2" />
        </CardHeader>
        <CardContent>
          {totalPercent !== 100 && accounts.length > 1 && (
            <p className="text-sm text-amber-600 mb-4" data-testid="allocation-warning">
              Total allocation: {totalPercent}% (should be 100%)
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {ACCOUNT_TYPES.map((type) => {
              const assigned = getAssignedAccount(type.value);
              const typeInfo = accountTypes[type.value] || {};

              return (
                <div
                  key={type.value}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    assigned
                      ? 'border-[#1e3a5f]/30 bg-[#1e3a5f]/5'
                      : 'border-dashed border-stone-300 bg-stone-50 hover:border-stone-400'
                  }`}
                  data-testid={`account-card-${type.value}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center ${assigned ? 'bg-[#1e3a5f]/10 text-[#1e3a5f]' : 'bg-stone-200 text-stone-400'}`}>
                      <AccountIcon name={type.icon} className="w-4 h-4" />
                    </div>
                    {assigned && (
                      <Badge variant="outline" className="text-xs bg-white">
                        {assigned.allocation_percent || 0}%
                      </Badge>
                    )}
                  </div>
                  <h4 className="font-medium text-sm text-stone-900 mb-1">{type.label}</h4>
                  {assigned ? (
                    <div className="space-y-1">
                      <p className="text-xs text-stone-600 truncate">
                        {assigned.manual_account_name || "Plaid Account"}
                      </p>
                      {assigned.manual_account_mask && (
                        <p className="text-xs text-stone-400">{assigned.manual_account_mask}</p>
                      )}
                      {assigned.current_balance !== undefined && assigned.current_balance !== null && (
                        <p className="text-xs font-medium text-[#1e3a5f]">
                          {formatCurrency(assigned.current_balance)}
                        </p>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-red-600 h-6 px-2 mt-1"
                        onClick={() => handleDeleteAccount(assigned.id)}
                        data-testid={`remove-account-${type.value}`}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 px-2 mt-2 w-full"
                      onClick={() => {
                        setSelectedType(type.value);
                        setFormData({
                          ...formData,
                          allocation_percent: typeInfo.default_percent?.toString() || ""
                        });
                        setShowAddDialog(true);
                      }}
                      data-testid={`assign-account-${type.value}`}
                    >
                      + Assign Account
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Transfer Tabs */}
      <Tabs defaultValue="pending">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending Transfers</TabsTrigger>
          <TabsTrigger value="quick" data-testid="tab-quick">Quick Transfers</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        {/* Pending Allocations Tab */}
        <TabsContent value="pending" className="space-y-4">
          {allocations.filter(a => !a.all_completed).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <AccountIcon name="Zap" className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-stone-900 mb-2">All Caught Up!</h3>
                <p className="text-stone-600 text-sm mb-4">No pending transfers. Run a new allocation when you receive revenue.</p>
                <Button variant="outline" onClick={() => setShowAllocationDialog(true)} disabled={accounts.length < 2} data-testid="run-allocation-empty-btn">
                  Run Allocation
                </Button>
              </CardContent>
            </Card>
          ) : (
            allocations.filter(a => !a.all_completed).map(allocation => (
              <Card key={allocation.id} className="overflow-hidden" data-testid={`allocation-${allocation.id}`}>
                <CardHeader className="bg-gradient-to-r from-stone-50 to-stone-100 py-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        Allocation - {allocation.allocation_date}
                        {allocation.auto_generated && <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Auto</Badge>}
                      </CardTitle>
                      <CardDescription>Total: {formatCurrency(allocation.total_revenue)}</CardDescription>
                    </div>
                    <Badge variant="outline">
                      {allocation.transfers.filter(t => t.completed).length}/{allocation.transfers.length} Done
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {allocation.transfers.map(transfer => {
                      const typeInfo = getAccountTypeInfo(transfer.account_type);
                      return (
                        <div
                          key={transfer.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${transfer.completed ? 'bg-[#1e3a5f]/5' : 'bg-stone-50'}`}
                          data-testid={`transfer-item-${transfer.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={transfer.completed}
                              onCheckedChange={(checked) => handleTransferComplete(transfer.id, checked)}
                              className="data-[state=checked]:bg-[#1e3a5f]"
                              data-testid={`transfer-check-${transfer.id}`}
                            />
                            <div>
                              <p className={`font-medium text-sm ${transfer.completed ? 'line-through text-stone-400' : 'text-stone-900'}`}>
                                {typeInfo && <AccountIcon name={typeInfo.icon} className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                                {transfer.account_name}
                              </p>
                              <p className="text-xs text-stone-500">
                                {transfer.institution} {transfer.account_mask && `${transfer.account_mask}`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${transfer.completed ? 'text-[#1e3a5f]' : 'text-stone-900'}`}>
                              {formatCurrency(transfer.amount)}
                            </p>
                            <p className="text-xs text-stone-500">{transfer.percent}%</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Quick Transfers Tab */}
        <TabsContent value="quick" className="space-y-4">
          {quickTransfers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                  <AccountIcon name="ArrowRight" className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="font-semibold text-stone-900 mb-2">No Quick Transfers Yet</h3>
                <p className="text-stone-600 text-sm mb-4">Move funds between your smart accounts instantly.</p>
                <Button variant="outline" onClick={() => setShowQuickTransferDialog(true)} disabled={configuredNonIncome.length < 2} data-testid="quick-transfer-empty-btn">
                  Make a Transfer
                </Button>
              </CardContent>
            </Card>
          ) : (
            quickTransfers.map(qt => {
              const fromInfo = getAccountTypeInfo(qt.from_account_type);
              const toInfo = getAccountTypeInfo(qt.to_account_type);
              return (
                <Card key={qt.id} data-testid={`quick-transfer-${qt.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-red-50 text-red-700 text-xs font-medium">
                            {fromInfo && <AccountIcon name={fromInfo.icon} className="w-3.5 h-3.5" />}
                            {fromInfo?.label || qt.from_account_name}
                          </div>
                          <AccountIcon name="ArrowRight" className="w-4 h-4 text-stone-400" />
                          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                            {toInfo && <AccountIcon name={toInfo.icon} className="w-3.5 h-3.5" />}
                            {toInfo?.label || qt.to_account_name}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#1e3a5f]">{formatCurrency(qt.amount)}</p>
                        <p className="text-xs text-stone-500">{new Date(qt.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    {qt.notes && <p className="text-xs text-stone-500 mt-2 pl-1">{qt.notes}</p>}
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          {allocations.filter(a => a.all_completed).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-stone-600">No completed allocations yet.</p>
              </CardContent>
            </Card>
          ) : (
            allocations.filter(a => a.all_completed).map(allocation => (
              <Card key={allocation.id} data-testid={`history-${allocation.id}`}>
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-stone-900 flex items-center gap-2">
                        {allocation.allocation_date}
                        {allocation.auto_generated && <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Auto</Badge>}
                      </p>
                      <p className="text-sm text-stone-500">{allocation.transfers.length} transfers completed</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#1e3a5f]">{formatCurrency(allocation.total_revenue)}</p>
                      <Badge className="bg-[#1e3a5f]/10 text-[#1e3a5f] border-0">Complete</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Add Account Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign {accountTypes[selectedType]?.name}</DialogTitle>
            <DialogDescription>{accountTypes[selectedType]?.description}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddAccount} className="space-y-4">
            <div className="space-y-2">
              <Label>Account Source</Label>
              <Select value={formData.account_source} onValueChange={(v) => setFormData({...formData, account_source: v})}>
                <SelectTrigger data-testid="account-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual_bank" disabled={manualBankAccounts.length === 0}>My Bank Accounts {manualBankAccounts.length === 0 && "(Add accounts first)"}</SelectItem>
                  <SelectItem value="plaid" disabled={linkedAccounts.length === 0}>Plaid-Linked {linkedAccounts.length === 0 && "(None connected)"}</SelectItem>
                  <SelectItem value="manual">Manual Entry</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.account_source === "manual_bank" ? (
              <div className="space-y-2">
                <Label>Select Bank Account *</Label>
                <Select value={formData.manual_bank_account_id} onValueChange={(v) => setFormData({...formData, manual_bank_account_id: v})}>
                  <SelectTrigger data-testid="bank-account-select"><SelectValue placeholder="Select from your accounts..." /></SelectTrigger>
                  <SelectContent>
                    {manualBankAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.account_name} - {acc.bank_name} {acc.account_number_last4 && `(${acc.account_number_last4})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : formData.account_source === "plaid" ? (
              <div className="space-y-2">
                <Label>Select Linked Account</Label>
                <Select value={formData.plaid_account_id} onValueChange={(v) => setFormData({...formData, plaid_account_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select account..." /></SelectTrigger>
                  <SelectContent>
                    {linkedAccounts.map(acc => (
                      <SelectItem key={acc.plaid_account_id} value={acc.plaid_account_id}>
                        {acc.name} - {acc.institution_name} ({acc.mask})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Account Nickname *</Label>
                  <Input placeholder="e.g., Chase Checking - Profit" value={formData.manual_account_name} onChange={(e) => setFormData({...formData, manual_account_name: e.target.value})} required data-testid="manual-account-name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Last 4 Digits</Label>
                    <Input placeholder="1234" maxLength={4} value={formData.manual_account_mask} onChange={(e) => setFormData({...formData, manual_account_mask: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Bank Name</Label>
                    <Input placeholder="Chase" value={formData.manual_institution} onChange={(e) => setFormData({...formData, manual_institution: e.target.value})} />
                  </div>
                </div>
              </>
            )}

            {selectedType !== "income" && (
              <div className="space-y-2">
                <Label>Allocation Percentage *</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" max="100" placeholder={accountTypes[selectedType]?.default_percent?.toString()} value={formData.allocation_percent} onChange={(e) => setFormData({...formData, allocation_percent: e.target.value})} required data-testid="allocation-percent-input" />
                  <span className="text-stone-500">%</span>
                </div>
                <p className="text-xs text-stone-500">Recommended: {accountTypes[selectedType]?.target_percent_range?.join("-")}%</p>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="assign-account-submit">Assign Account</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Run Allocation Dialog */}
      <Dialog open={showAllocationDialog} onOpenChange={setShowAllocationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Run Revenue Allocation</DialogTitle>
            <DialogDescription>Enter the total revenue to allocate from your Income account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Revenue Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500">$</span>
                <Input type="number" min="0" step="0.01" className="pl-8" placeholder="10000" value={allocationAmount} onChange={(e) => setAllocationAmount(e.target.value)} data-testid="allocation-amount-input" />
              </div>
            </div>
            {allocationAmount && parseFloat(allocationAmount) > 0 && (
              <div className="p-4 rounded-lg bg-stone-50 space-y-2" data-testid="allocation-preview">
                <p className="text-sm font-medium text-stone-700">Allocation Preview:</p>
                {accounts.filter(a => a.account_type !== "income").map(acc => {
                  const amount = parseFloat(allocationAmount) * (acc.allocation_percent / 100);
                  const typeInfo = getAccountTypeInfo(acc.account_type);
                  return (
                    <div key={acc.id} className="flex justify-between text-sm">
                      <span className="text-stone-600 flex items-center gap-1.5">
                        {typeInfo && <AccountIcon name={typeInfo.icon} className="w-3.5 h-3.5" />}
                        {typeInfo?.label}
                      </span>
                      <span className="font-medium">{formatCurrency(amount)} ({acc.allocation_percent}%)</span>
                    </div>
                  );
                })}
                <Separator className="my-2" />
                <div className="flex justify-between text-sm font-bold">
                  <span>Total</span>
                  <span>{formatCurrency(parseFloat(allocationAmount) * (totalPercent / 100))}</span>
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowAllocationDialog(false)}>Cancel</Button>
              <Button className="flex-1 bg-[#1e3a5f] hover:bg-[#2d5a8e]" onClick={handleCreateAllocation} disabled={!allocationAmount || parseFloat(allocationAmount) <= 0} data-testid="create-allocation-submit">
                Create Allocation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Transfer Dialog */}
      <Dialog open={showQuickTransferDialog} onOpenChange={setShowQuickTransferDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Quick Transfer</DialogTitle>
            <DialogDescription>Move funds between your smart accounts instantly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>From Account</Label>
              <Select value={quickTransferForm.from_account_type} onValueChange={(v) => setQuickTransferForm({...quickTransferForm, from_account_type: v})}>
                <SelectTrigger data-testid="quick-from-select"><SelectValue placeholder="Select source..." /></SelectTrigger>
                <SelectContent>
                  {accounts.map(acc => {
                    const typeInfo = getAccountTypeInfo(acc.account_type);
                    return (
                      <SelectItem key={acc.id} value={acc.account_type} disabled={acc.account_type === quickTransferForm.to_account_type}>
                        {typeInfo?.label} — {acc.manual_account_name || 'Plaid'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-center">
              <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                <AccountIcon name="ArrowRight" className="w-4 h-4 text-stone-400 rotate-90" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>To Account</Label>
              <Select value={quickTransferForm.to_account_type} onValueChange={(v) => setQuickTransferForm({...quickTransferForm, to_account_type: v})}>
                <SelectTrigger data-testid="quick-to-select"><SelectValue placeholder="Select destination..." /></SelectTrigger>
                <SelectContent>
                  {accounts.map(acc => {
                    const typeInfo = getAccountTypeInfo(acc.account_type);
                    return (
                      <SelectItem key={acc.id} value={acc.account_type} disabled={acc.account_type === quickTransferForm.from_account_type}>
                        {typeInfo?.label} — {acc.manual_account_name || 'Plaid'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500">$</span>
                <Input type="number" min="0" step="0.01" className="pl-8" placeholder="500" value={quickTransferForm.amount} onChange={(e) => setQuickTransferForm({...quickTransferForm, amount: e.target.value})} data-testid="quick-amount-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input placeholder="e.g., Cover tax shortfall" value={quickTransferForm.notes} onChange={(e) => setQuickTransferForm({...quickTransferForm, notes: e.target.value})} data-testid="quick-notes-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowQuickTransferDialog(false)}>Cancel</Button>
              <Button
                className="flex-1 bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                onClick={handleQuickTransfer}
                disabled={!quickTransferForm.from_account_type || !quickTransferForm.to_account_type || !quickTransferForm.amount || parseFloat(quickTransferForm.amount) <= 0}
                data-testid="quick-transfer-submit"
              >
                Transfer Funds
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-Transfer Settings Dialog */}
      <Dialog open={showAutoSettingsDialog} onOpenChange={setShowAutoSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Automatic Allocations</DialogTitle>
            <DialogDescription>Configure recurring allocation checklists on the 10th and 25th of each month.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-stone-50">
              <div>
                <p className="font-medium text-stone-900 text-sm">Enable Auto-Allocations</p>
                <p className="text-xs text-stone-500">Creates transfer checklists automatically</p>
              </div>
              <Switch checked={autoSettingsForm.enabled} onCheckedChange={(v) => setAutoSettingsForm({...autoSettingsForm, enabled: v})} data-testid="auto-transfer-toggle" />
            </div>

            {autoSettingsForm.enabled && (
              <>
                <div className="space-y-2">
                  <Label>Default Revenue Amount Per Cycle</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500">$</span>
                    <Input type="number" min="0" step="0.01" className="pl-8" placeholder="10000" value={autoSettingsForm.default_revenue_amount} onChange={(e) => setAutoSettingsForm({...autoSettingsForm, default_revenue_amount: e.target.value})} data-testid="auto-revenue-input" />
                  </div>
                  <p className="text-xs text-stone-500">This amount will be used to calculate allocations on each cycle.</p>
                </div>

                <div className="p-4 rounded-lg bg-blue-50/50 border border-blue-100">
                  <div className="flex items-start gap-2">
                    <AccountIcon name="Calendar" className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-800">
                      <p className="font-medium mb-1">Schedule</p>
                      <p>Allocations will be created on the <strong>10th</strong> and <strong>25th</strong> of each month at 8:00 AM UTC. You'll still need to manually confirm each transfer.</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowAutoSettingsDialog(false)}>Cancel</Button>
              <Button
                className="flex-1 bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                onClick={handleSaveAutoSettings}
                disabled={savingAutoSettings || (autoSettingsForm.enabled && (!autoSettingsForm.default_revenue_amount || parseFloat(autoSettingsForm.default_revenue_amount) <= 0))}
                data-testid="save-auto-settings-btn"
              >
                {savingAutoSettings ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfitFirstAccounts;
